import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { booleanAttribute, Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DMS_ADMIN_ONLY_MESSAGE, AuthorityCode, SessionService, UserRole } from '../../core/services/session.service';
import { ToastService } from '../../core/portal/toast.service';
import { SbcApiService } from '../../modules/enforcement/services/sbc-api.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  private readonly session = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly api = inject(SbcApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  @Input({ transform: booleanAttribute }) overlay = false;
  @Input() returnUrl = '';
  @Output() closed = new EventEmitter<void>();

  error = signal('');
  info = signal('');
  busy = signal(false);

  form = {
    userId: 'officer',
    authorityId: 'PDA' as AuthorityCode,
    password: '',
  };

  close(): void {
    this.closed.emit();
  }

  private postLoginPath(): string {
    const fromInput = this.returnUrl.trim();
    if (fromInput.startsWith('/') && !fromInput.startsWith('//')) {
      return fromInput;
    }
    const fromRoute = this.route.snapshot.queryParamMap.get('returnUrl');
    if (fromRoute && fromRoute.startsWith('/') && !fromRoute.startsWith('//')) {
      return fromRoute;
    }
    return this.session.homePath();
  }

  private isDmsPath(url: string): boolean {
    return url === '/dms' || url.startsWith('/dms/');
  }

  signIn(): void {
    this.error.set('');
    this.info.set('');
    const userCode = this.form.userId.trim();
    if (!userCode) {
      this.error.set('User ID is required');
      return;
    }
    if (!this.form.password) {
      this.error.set('Password is required');
      return;
    }
    if (this.form.password.length < 6) {
      this.error.set('Password must be at least 6 characters');
      return;
    }

    this.busy.set(true);
    this.api
      .login({
        userCode,
        authorityCode: this.form.authorityId,
        password: this.form.password,
      })
      .subscribe({
        next: (res) => {
          const u = res.data;
          if (!u.accessToken) {
            this.busy.set(false);
            this.error.set('Login succeeded but no access token — restart the backend and try again.');
            return;
          }
          this.session.login({
            userId: u.userId || u.userCode,
            displayName: u.displayName,
            authorityId: u.authorityCode as AuthorityCode,
            role: u.role as UserRole,
            accessToken: u.accessToken,
            expiresIn: u.expiresIn,
          });
          if (u.passwordSetNow) {
            this.info.set('Password saved. You can use the same password next time.');
          }
          this.busy.set(false);
          const next = this.postLoginPath();
          if (this.isDmsPath(next) && !this.session.canAccessDms()) {
            this.toast.showError(DMS_ADMIN_ONLY_MESSAGE);
            void this.router.navigateByUrl('/');
            return;
          }
          void this.router.navigateByUrl(next);
        },
        error: (err: HttpErrorResponse) => {
          this.busy.set(false);
          const msg =
            err.error?.message ||
            err.error?.error ||
            (err.status === 0
              ? 'Cannot reach API — open http://localhost:8080 (Docker) or start backend on port 3000'
              : `Login failed (${err.status})`);
          this.error.set(msg);
        },
      });
  }
}
